import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { UserRound, FileText, TriangleAlert, CheckCircle2, Link2, Plus, Pencil, Trash2, Users, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, API, fmtDate, withToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

function overallStatus(items) {
    if (items.some((i) => i.status === "mal")) return { label: "Requiere atención", cls: "bg-rose-100 text-rose-700" };
    if (items.some((i) => i.status === "regular")) return { label: "Con observaciones", cls: "bg-amber-100 text-amber-700" };
    return { label: "Todo en orden", cls: "bg-emerald-100 text-emerald-700" };
}

export default function DriverHistory() {
    const { isAdmin, canWrite } = useAuth();
    const [checklists, setChecklists] = useState([]);
    const [cars, setCars] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadDrivers = () => api.get("/drivers").then((r) => setDrivers(r.data));

    useEffect(() => {
        Promise.all([api.get("/checklists/public-submissions"), api.get("/cars"), api.get("/drivers")])
            .then(([clRes, carsRes, driversRes]) => {
                setChecklists(clRes.data);
                setCars(carsRes.data);
                setDrivers(driversRes.data);
            })
            .finally(() => setLoading(false));
    }, []);

    const normalizeName = (n) => (n || "").trim().toLowerCase();

    const findDriver = (name) => drivers.find((d) => normalizeName(d.name) === normalizeName(name));

    const quickAddDriver = async (name) => {
        try {
            await api.post("/drivers", { name, phone: "", active: true });
            toast.success(`${name} agregado al directorio. Edita su teléfono cuando quieras.`);
            loadDrivers();
        } catch {
            toast.error("No se pudo agregar al directorio");
        }
    };

    const carLabel = (id) => {
        const c = cars.find((c) => c.id === id);
        return c ? `${c.plate} · ${c.brand} ${c.model}` : "—";
    };

    const groups = useMemo(() => {
        const byDriver = {};
        checklists.forEach((c) => {
            const name = c.created_by || "Sin nombre";
            if (!byDriver[name]) byDriver[name] = [];
            byDriver[name].push(c);
        });
        return Object.entries(byDriver)
            .map(([name, items]) => ({ name, items }))
            .sort((a, b) => a.name.localeCompare(b.name, "es"));
    }, [checklists]);

    const alerts = useMemo(() => {
        return checklists
            .filter((c) => c.items.some((i) => i.status === "mal"))
            .sort((a, b) => (a.date < b.date ? 1 : -1));
    }, [checklists]);

    const toggleResolved = async (checklist) => {
        try {
            const { data } = await api.patch(`/checklists/${checklist.id}/resolve`, { resolved: !checklist.resolved });
            setChecklists((prev) => prev.map((c) => (c.id === data.id ? data : c)));
            toast.success(data.resolved ? "Alerta marcada como atendida" : "Alerta marcada como pendiente");
        } catch {
            toast.error("No se pudo actualizar la alerta");
        }
    };

    const copyDriverLink = async () => {
        const link = `${window.location.origin}/revision-conductor`;
        try {
            await navigator.clipboard.writeText(link);
            toast.success("Enlace copiado. Compártelo con tus conductores.");
        } catch {
            toast.error(`No se pudo copiar. Enlace: ${link}`);
        }
    };

    return (
        <div className="space-y-6" data-testid="driver-history-page">
            <header className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Revisiones enviadas por conductores</p>
                    <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mt-1 font-display">
                        Historial por conductor
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Revisiones físico-mecánicas enviadas desde el formulario público, agrupadas por quien las llenó.
                    </p>
                </div>
                <Button variant="outline" onClick={copyDriverLink} className="gap-2" data-testid="copy-driver-link-button">
                    <Link2 size={16} /> Copiar enlace para conductores
                </Button>
            </header>

            <Tabs defaultValue="drivers" className="w-full">
                <TabsList data-testid="driver-history-tabs">
                    <TabsTrigger value="drivers" data-testid="tab-por-conductor">Por conductor</TabsTrigger>
                    <TabsTrigger value="alerts" data-testid="tab-alertas-urgentes">
                        Alertas urgentes {alerts.length > 0 && <span className="ml-1.5 rounded-full bg-rose-500 text-white text-[10px] px-1.5 py-0.5">{alerts.length}</span>}
                    </TabsTrigger>
                    <TabsTrigger value="directory" data-testid="tab-directorio-conductores">Directorio</TabsTrigger>
                </TabsList>

                <TabsContent value="drivers">
                    <Card className="border-slate-200 shadow-sm">
                        <CardHeader>
                            <CardTitle className="text-lg font-display font-medium">Conductores ({groups.length})</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            {loading ? (
                                <div className="px-6 py-10 text-center text-slate-400 text-sm">Cargando…</div>
                            ) : groups.length === 0 ? (
                                <div className="px-6 py-10 text-center text-slate-400 text-sm">
                                    Todavía no hay revisiones enviadas por conductores.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                                <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                                                <th className="text-left px-4 py-3 font-semibold">Auto</th>
                                                <th className="text-left px-4 py-3 font-semibold">Kilometraje</th>
                                                <th className="text-left px-4 py-3 font-semibold">Estado general</th>
                                                <th className="px-4 py-3" />
                                            </tr>
                                        </thead>
                                        {groups.map((g, idx) => {
                                            const linkedDriver = findDriver(g.name);
                                            const testidName = g.name.trim().toLowerCase().replace(/\s+/g, "-");
                                            return (
                                            <tbody key={g.name} className={idx > 0 ? "border-t-8 border-slate-100" : undefined} data-testid={`driver-group-${g.name}`}>
                                                <tr className="bg-slate-100/70">
                                                    <td colSpan={5} className="px-4 py-2">
                                                        <span className="font-semibold text-slate-800 flex items-center gap-1.5 flex-wrap">
                                                            <UserRound size={14} className="text-slate-500" /> {g.name}
                                                            <span className="text-xs text-slate-500 font-normal">({g.items.length} revisión{g.items.length !== 1 ? "es" : ""})</span>
                                                            {linkedDriver ? (
                                                                linkedDriver.phone && (
                                                                    <span className="inline-flex items-center gap-1 text-xs text-slate-500 font-normal" data-testid={`driver-phone-${testidName}`}>
                                                                        <Phone size={11} /> {linkedDriver.phone}
                                                                    </span>
                                                                )
                                                            ) : (
                                                                canWrite && (
                                                                    <button
                                                                        onClick={() => quickAddDriver(g.name)}
                                                                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 border border-slate-300 rounded-full px-2 py-0.5"
                                                                        data-testid={`quick-add-driver-${testidName}`}
                                                                    >
                                                                        <Plus size={11} /> Agregar al directorio
                                                                    </button>
                                                                )
                                                            )}
                                                        </span>
                                                    </td>
                                                </tr>
                                                {g.items.map((c) => {
                                                    const st = overallStatus(c.items);
                                                    return (
                                                        <tr key={c.id} className="border-t border-slate-100" data-testid={`driver-checklist-${c.id}`}>
                                                            <td className="px-4 py-3 text-slate-900 font-medium">{fmtDate(c.date)}</td>
                                                            <td className="px-4 py-3 text-slate-700">
                                                                <Link to={`/autos/${c.car_id}`} className="hover:underline">{carLabel(c.car_id)}</Link>
                                                            </td>
                                                            <td className="px-4 py-3 text-slate-700">{c.mileage != null ? `${Number(c.mileage).toLocaleString("es-MX")} km` : "—"}</td>
                                                            <td className="px-4 py-3"><Badge className={`${st.cls} hover:${st.cls}`}>{st.label}</Badge></td>
                                                            <td className="px-4 py-3 text-right">
                                                                <a
                                                                    href={withToken(`${API}/checklists/${c.id}/export/pdf`)}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="p-1.5 rounded hover:bg-slate-100 inline-flex"
                                                                    data-testid={`driver-checklist-export-${c.id}`}
                                                                >
                                                                    <FileText size={14} />
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            );
                                        })}
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="alerts">
                    <Card className="border-slate-200 shadow-sm">
                        <CardHeader>
                            <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                                <TriangleAlert size={18} className="text-rose-500" /> Alertas urgentes ({alerts.length})
                            </CardTitle>
                            <p className="text-xs text-slate-500">Revisiones con al menos un punto en estado "Mal".</p>
                        </CardHeader>
                        <CardContent className="p-0">
                            {loading ? (
                                <div className="px-6 py-10 text-center text-slate-400 text-sm">Cargando…</div>
                            ) : alerts.length === 0 ? (
                                <div className="px-6 py-10 text-center text-slate-400 text-sm" data-testid="no-alerts-message">
                                    No hay alertas urgentes registradas.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                                <th className="text-left px-4 py-3 font-semibold">Fecha</th>
                                                <th className="text-left px-4 py-3 font-semibold">Auto</th>
                                                <th className="text-left px-4 py-3 font-semibold">Conductor</th>
                                                <th className="text-left px-4 py-3 font-semibold">Puntos en mal estado</th>
                                                <th className="text-left px-4 py-3 font-semibold">Estado</th>
                                                <th className="px-4 py-3" />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {alerts.map((c) => {
                                                const badPoints = c.items.filter((i) => i.status === "mal").map((i) => i.label);
                                                return (
                                                    <tr key={c.id} className="border-t border-slate-100" data-testid={`alert-row-${c.id}`}>
                                                        <td className="px-4 py-3 text-slate-900 font-medium">{fmtDate(c.date)}</td>
                                                        <td className="px-4 py-3 text-slate-700">
                                                            <Link to={`/autos/${c.car_id}`} className="hover:underline">{carLabel(c.car_id)}</Link>
                                                        </td>
                                                        <td className="px-4 py-3 text-slate-700">{c.created_by || "Sin nombre"}</td>
                                                        <td className="px-4 py-3 text-slate-700">{badPoints.join(", ")}</td>
                                                        <td className="px-4 py-3">
                                                            {c.resolved ? (
                                                                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid={`alert-badge-atendida-${c.id}`}>Atendida</Badge>
                                                            ) : (
                                                                <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100" data-testid={`alert-badge-pendiente-${c.id}`}>Pendiente</Badge>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                                            {isAdmin && (
                                                                <Button
                                                                    size="sm"
                                                                    variant={c.resolved ? "outline" : "default"}
                                                                    className={c.resolved ? "" : "bg-rose-600 hover:bg-rose-700"}
                                                                    onClick={() => toggleResolved(c)}
                                                                    data-testid={`alert-resolve-toggle-${c.id}`}
                                                                >
                                                                    <CheckCircle2 size={14} className="mr-1" />
                                                                    {c.resolved ? "Marcar pendiente" : "Marcar atendida"}
                                                                </Button>
                                                            )}
                                                            <a
                                                                href={withToken(`${API}/checklists/${c.id}/export/pdf`)}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="p-1.5 rounded hover:bg-slate-100 inline-flex ml-1"
                                                                data-testid={`alert-export-${c.id}`}
                                                            >
                                                                <FileText size={14} />
                                                            </a>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="directory">
                    <DriversDirectoryPanel />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function DriversDirectoryPanel() {
    const { isAdmin, canWrite } = useAuth();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const empty = { name: "", phone: "", active: true };
    const [form, setForm] = useState(empty);

    const load = () => api.get("/drivers").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
    const openEdit = (d) => {
        setEditing(d);
        setForm({ name: d.name, phone: d.phone || "", active: d.active });
        setOpen(true);
    };
    const submit = async () => {
        if (!form.name.trim()) return toast.error("El nombre es obligatorio");
        try {
            if (editing) await api.put(`/drivers/${editing.id}`, form);
            else await api.post("/drivers", form);
            toast.success("Guardado");
            setOpen(false);
            load();
        } catch {
            toast.error("Error al guardar");
        }
    };
    const remove = async (d) => {
        if (!window.confirm(`¿Eliminar a ${d.name} del directorio?`)) return;
        await api.delete(`/drivers/${d.id}`);
        toast.success("Eliminado");
        load();
    };

    return (
        <Card className="border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                        <Users size={18} className="text-slate-500" /> Directorio de conductores ({items.length})
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">
                        Guarda nombre y teléfono de tus conductores. Listo para cuando activemos recordatorios automáticos por WhatsApp/SMS.
                    </p>
                </div>
                {canWrite && (
                    <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-driver-button">
                        <Plus size={16} className="mr-1" /> Nuevo conductor
                    </Button>
                )}
            </CardHeader>
            <CardContent className="p-0">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                <th className="text-left px-4 py-3 font-semibold">Nombre</th>
                                <th className="text-left px-4 py-3 font-semibold">Teléfono</th>
                                <th className="text-left px-4 py-3 font-semibold">Estado</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={4} className="text-center px-4 py-10 text-slate-400">Sin conductores registrados</td></tr>
                            )}
                            {items.map((d) => (
                                <tr key={d.id} className="border-t border-slate-100" data-testid={`driver-directory-row-${d.id}`}>
                                    <td className="px-4 py-3 text-slate-900 font-medium">{d.name}</td>
                                    <td className="px-4 py-3 text-slate-700">
                                        {d.phone ? (
                                            <span className="inline-flex items-center gap-1"><Phone size={12} /> {d.phone}</span>
                                        ) : "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge className={d.active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>
                                            {d.active ? "Activo" : "Inactivo"}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-1">
                                            {canWrite && (
                                                <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(d)} data-testid={`edit-driver-${d.id}`}><Pencil size={14} /></button>
                                            )}
                                            {isAdmin && (
                                                <button className="p-1.5 rounded hover:bg-rose-50 text-rose-600" onClick={() => remove(d)} data-testid={`delete-driver-${d.id}`}><Trash2 size={14} /></button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </CardContent>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="driver-directory-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? "Editar conductor" : "Nuevo conductor"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Nombre</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="driver-name-input" />
                        </div>
                        <div>
                            <Label className="text-xs">Teléfono</Label>
                            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Ej. 4421234567" data-testid="driver-phone-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={submit} data-testid="save-driver-button">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
